/**
 * Helpers for displaying who marked attendance on audit tiles / dialogs.
 */

function getLogMetadata(log) {
  const meta = log?.request_metadata;
  return meta && typeof meta === 'object' ? meta : {};
}

export function getVisionReviewFromLog(log) {
  const meta = getLogMetadata(log);
  if (meta.vision_review && typeof meta.vision_review === 'object') {
    return meta.vision_review;
  }
  if (log?.ai_review && typeof log.ai_review === 'object') {
    return log.ai_review;
  }
  return null;
}

export function isLogAiReviewed(log) {
  const vr = getVisionReviewFromLog(log);
  return Boolean(vr?.reviewed);
}

export function isAttendanceMarkedByAI(log) {
  if (log?.manual_attendance?.marked_by_ai === true) return true;
  const vr = getVisionReviewFromLog(log);
  if (vr?.auto_attendance?.success === true) return true;
  return false;
}

export function getAttendanceMarkedByLabel(log) {
  if (isAttendanceMarkedByAI(log)) {
    return 'Attendance marked by AI';
  }
  const name = log?.manual_attendance?.marked_by_name;
  if (!name) return null;
  return `Attendance marked by ${name}`;
}

export function hasAttendanceMarkedBy(log) {
  return Boolean(getAttendanceMarkedByLabel(log));
}

export function isAttendanceSent(value) {
  return value === true || value === 1 || value === '1' || value === 'true';
}

export function isAttendanceRevokedOnLog(log) {
  if (isAttendanceSent(log?.send_attendance)) {
    return false;
  }
  const meta = getLogMetadata(log);
  const attendanceMeta = meta.attendance && typeof meta.attendance === 'object' ? meta.attendance : null;
  const attendanceRevocation = meta.attendance_revocation && typeof meta.attendance_revocation === 'object'
    ? meta.attendance_revocation
    : null;
  return Boolean(
    log?.revoked === true
    || (attendanceMeta && attendanceMeta.revoked)
    || (attendanceRevocation && attendanceRevocation.revoked)
    || meta.attendance_revoked
  );
}

export function getAttendanceReopenInfo(log) {
  const meta = getLogMetadata(log);
  const reopen = meta.attendance_reopen && typeof meta.attendance_reopen === 'object'
    ? meta.attendance_reopen
    : null;
  if (!reopen || !isAttendanceRevokedOnLog(log)) return null;
  return reopen;
}

export function isAttendanceRevokedReopenable(log) {
  const reopen = getAttendanceReopenInfo(log);
  if (!reopen) return false;
  return Boolean(reopen.can_re_mark || reopen.can_re_review);
}

export function getAttendanceRevokedReopenHint(log) {
  const reopen = getAttendanceReopenInfo(log);
  if (reopen?.message) return reopen.message;
  if (!isAttendanceRevokedReopenable(log)) return null;
  const parts = [];
  if (reopen?.can_re_mark) parts.push('mark attendance again');
  if (reopen?.can_re_review) parts.push('re-run AI matching');
  if (parts.length === 0) return null;
  return `Attendance revoked — you may ${parts.join(' or ')}.`;
}

export function getAiReviewChipLabel(log) {
  const vr = getVisionReviewFromLog(log);
  if (!vr?.reviewed) return null;
  if (vr.error) return 'AI matching failed';
  if (vr.best_candidate_name) {
    const conf = vr.confidence != null ? ` (${vr.confidence}%)` : '';
    return `AI: ${vr.best_candidate_name}${conf}`;
  }
  return 'AI matched';
}

const SYSTEM_REQUESTERS = new Set(['realtime_auto', 'scheduler', 'recent_attendance_gate', 'system']);

const AUTOMATION_MODE_LABELS = {
  manual: 'Manual',
  realtime: 'Realtime',
  scheduled: 'Scheduled',
};

export function getVisionReviewExecutorInfo(log) {
  const vr = getVisionReviewFromLog(log);
  if (!vr?.reviewed) return null;

  const storedName = String(vr.triggered_by_user_name || '').trim();
  const requester = String(vr.reviewed_by || '').trim();
  let displayName = storedName;
  if (!displayName) {
    if (SYSTEM_REQUESTERS.has(requester)) {
      displayName = 'System';
    } else if (requester) {
      displayName = requester;
    }
  }

  const mode = String(vr.automation_mode || '').trim().toLowerCase();
  const modeLabel = AUTOMATION_MODE_LABELS[mode]
    || (requester === 'realtime_auto' ? 'Realtime' : requester === 'scheduler' ? 'Scheduled' : requester ? 'Manual' : null);

  if (!displayName && !modeLabel) return null;

  return {
    triggeredByUserId: vr.triggered_by_user_id || null,
    triggeredByUserName: displayName || '—',
    automationMode: mode || null,
    automationModeLabel: modeLabel || '—',
  };
}
